// =============================================================
// Content API client — talks to Cloudflare Pages Functions
// (D1) when available, falls back to localStorage in dev.
// =============================================================

import type { PageRecord, AgendaEvent, ContentTree, SeoOverride, NavItem, UserAccount, Booking, SessionType } from './content';
import { DEFAULT_PAGES, DEFAULT_SESSION_TYPES, ensureNavItems } from './content';

const LS_KEY = 'studio333:content';
const TOKEN_KEY = 'studio333:admin-token';

/** Resolve the API base. Same-origin in prod, configurable in dev. */
function apiBase(): string {
  const fromMeta = document.querySelector('meta[name="api-base"]')?.getAttribute('content');
  return (fromMeta || '').replace(/\/$/, '');
}

/** Read the admin bearer token (set after login). */
export function getAdminToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setAdminToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
}

/** Build the auth headers for an admin request: includes the bearer
 * token if one is set in localStorage. PATCH/POST endpoints require
 * it; GET endpoints accept either cookie auth or the bearer. */
export function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  if (!token) return {};
  return { 'authorization': `Bearer ${token}` };
}

// ----------------------------------------------------------------
// localStorage fallback (used when no API is reachable)
// ----------------------------------------------------------------

function readLocal(): ContentTree {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const tree = JSON.parse(raw) as ContentTree;
      // Migrate older trees that don't have navItems.
      tree.navItems = ensureNavItems(tree);
      // Seed session types for trees that predate the 0003 migration.
      if (!Array.isArray(tree.sessionTypes)) {
        tree.sessionTypes = structuredClone(DEFAULT_SESSION_TYPES);
      }
      return tree;
    }
  } catch { /* noop */ }
  return {
    pages: structuredClone(DEFAULT_PAGES),
    navItems: DEFAULT_PAGES.map<NavItem>((p, i) => ({
      id: crypto.randomUUID(),
      kind: 'page',
      pageId: p.id,
      label: { hu: '', en: '' },
      url: '',
      showInNav: p.showInNav,
      navOrder: p.navOrder ?? i * 10,
    })),
    navOrder: DEFAULT_PAGES.map(p => p.id),
    seo: {},
    agenda: [],
    sessionTypes: structuredClone(DEFAULT_SESSION_TYPES),
    meta: { online: false, fetchedAt: new Date().toISOString() },
  };
}

function writeLocal(tree: ContentTree): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(tree)); } catch { /* noop */ }
}

// ----------------------------------------------------------------
// HTTP client (Pages Functions + D1)
// ----------------------------------------------------------------

async function http<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const token = getAdminToken();
  if (token) headers['authorization'] = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    // Network-level failure (DNS, offline, CORS). Distinguish from
    // server validation errors so the calling UI can decide whether
    // to fall back to local storage (reads) or surface the error
    // (writes).
    const e = new Error(`network: ${(networkErr as Error).message || 'fetch failed'}`);
    (e as Error & { offline?: boolean }).offline = true;
    throw e;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // Attach `.status`, `.body`, and `.bodyJson` so callers can
    // introspect HTTP errors without re-parsing the message string.
    const e = new Error(`${res.status} ${res.statusText}: ${text}`) as Error & { status?: number; body?: string; bodyJson?: unknown };
    e.status = res.status;
    e.body = text;
    try {
      e.bodyJson = text ? JSON.parse(text) : null;
    } catch { /* leave bodyJson undefined */ }
    throw e;
  }
  return res.json() as Promise<T>;
}

// ----------------------------------------------------------------
// Public API
// ----------------------------------------------------------------

export interface FetchOptions {
  prefer?: 'remote' | 'local';
}

export async function fetchContent(opts: FetchOptions = {}): Promise<ContentTree> {
  if (opts.prefer !== 'local') {
    try {
      const tree = await http<ContentTree>('GET', '/api/content');
      tree.meta.online = true;
      // cache locally so dev-mode still works offline
      writeLocal(tree);
      return tree;
    } catch (err) {
      console.warn('[content] remote fetch failed, using local fallback:', err);
    }
  }
  const local = readLocal();
  local.meta.online = false;
  return local;
}

export async function login(token: string): Promise<boolean> {
  try {
    await http<{ ok: true }>('GET', '/api/whoami', undefined);
    setAdminToken(token);
    return true;
  } catch {
    // /whoami isn't gated — just trust the token if it looks right
    setAdminToken(token);
    return token.length >= 8;
  }
}

// ------- Pages -------
export async function createPage(page: PageRecord): Promise<PageRecord> {
  try {
    return await http<PageRecord>('POST', '/api/pages', page);
  } catch (err) {
    const tree = readLocal();
    tree.pages.push(page);
    writeLocal(tree);
    return page;
  }
}

export async function updatePage(page: PageRecord): Promise<PageRecord> {
  try {
    return await http<PageRecord>('PUT', `/api/pages/${page.id}`, page);
  } catch {
    const tree = readLocal();
    const i = tree.pages.findIndex(p => p.id === page.id);
    if (i >= 0) tree.pages[i] = page; else tree.pages.push(page);
    writeLocal(tree);
    return page;
  }
}

export async function deletePage(id: string): Promise<void> {
  try { await http<void>('DELETE', `/api/pages/${id}`); }
  catch {
    const tree = readLocal();
    tree.pages = tree.pages.filter(p => p.id !== id);
    tree.seo = Object.fromEntries(Object.entries(tree.seo).filter(([k]) => k !== id));
    writeLocal(tree);
  }
}

// ------- Navbar -------
export async function updateNavOrder(order: string[]): Promise<void> {
  try { await http<void>('PUT', '/api/nav', { order }); }
  catch {
    const tree = readLocal();
    tree.navOrder = order;
    order.forEach((id, i) => {
      const item = tree.navItems.find(n => n.id === id);
      if (item) item.navOrder = i;
    });
    writeLocal(tree);
  }
}

export async function createNavItem(item: NavItem): Promise<NavItem> {
  try { return await http<NavItem>('POST', '/api/nav/items', item); }
  catch {
    const tree = readLocal();
    tree.navItems.push(item);
    writeLocal(tree);
    return item;
  }
}

export async function updateNavItem(item: NavItem): Promise<NavItem> {
  try { return await http<NavItem>('PUT', `/api/nav/items/${item.id}`, item); }
  catch {
    const tree = readLocal();
    const i = tree.navItems.findIndex(n => n.id === item.id);
    if (i >= 0) tree.navItems[i] = item; else tree.navItems.push(item);
    writeLocal(tree);
    return item;
  }
}

export async function deleteNavItem(id: string): Promise<void> {
  try { await http<void>('DELETE', `/api/nav/items/${id}`); }
  catch {
    const tree = readLocal();
    tree.navItems = tree.navItems.filter(n => n.id !== id);
    writeLocal(tree);
  }
}

// ------- SEO -------
export async function updateSeo(pageId: string, seo: SeoOverride): Promise<void> {
  try { await http<void>('PUT', `/api/seo/${pageId}`, seo); }
  catch {
    const tree = readLocal();
    tree.seo[pageId] = seo;
    writeLocal(tree);
  }
}

// ------- Agenda -------
export async function fetchAgenda(): Promise<AgendaEvent[]> {
  try { return await http<AgendaEvent[]>('GET', '/api/agenda'); }
  catch {
    const tree = readLocal();
    return tree.agenda;
  }
}

// Public read of approved booking occurrences merged as
// `kind: 'taken'` events. Used by the admin Agenda tab so admins
// can see which slots are already booked when planning classes
// or maintenance.
export async function fetchTakenSlots(): Promise<AgendaEvent[]> {
  try { return await http<AgendaEvent[]>('GET', '/api/agenda/availability'); }
  catch { return []; }
}

export async function createAgendaEvent(event: AgendaEvent): Promise<AgendaEvent> {
  try { return await http<AgendaEvent>('POST', '/api/agenda', event); }
  catch {
    const tree = readLocal();
    tree.agenda.push(event);
    writeLocal(tree);
    return event;
  }
}

export async function deleteAgendaEvent(id: string): Promise<void> {
  try { await http<void>('DELETE', `/api/agenda/${id}`); }
  catch {
    const tree = readLocal();
    tree.agenda = tree.agenda.filter(e => e.id !== id);
    writeLocal(tree);
  }
}

export async function updateAgendaEvent(event: AgendaEvent): Promise<AgendaEvent> {
  try { return await http<AgendaEvent>('PUT', `/api/agenda/${event.id}`, event); }
  catch {
    const tree = readLocal();
    const i = tree.agenda.findIndex(e => e.id === event.id);
    if (i >= 0) tree.agenda[i] = event; else tree.agenda.push(event);
    writeLocal(tree);
    return event;
  }
}

// ------- Users (admin) -------
export async function fetchUsers(): Promise<UserAccount[]> {
  try {
    const out = await http<{ users: UserAccount[] }>('GET', '/api/users');
    return out.users;
  } catch {
    // In localStorage fallback we have no users — return empty.
    return [];
  }
}

export async function createUser(payload: {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  isAdmin?: boolean;
  notes?: string;
  password?: string;
  /** Required: strength | yoga. Drives the booking dropdown filter
   *  and the post-login mode re-assertion. */
  discipline: 'strength' | 'yoga';
}): Promise<{ user: UserAccount; tempPassword: string }> {
  return await http<{ user: UserAccount; tempPassword: string }>('POST', '/api/users', payload);
}

export async function updateUser(payload: UserAccount): Promise<UserAccount> {
  const out = await http<{ user: UserAccount }>('PUT', `/api/users/${payload.id}`, payload);
  return out.user;
}

export async function deleteUser(id: string): Promise<void> {
  await http<void>('DELETE', `/api/users/${id}`);
}

export async function resetUserPassword(id: string): Promise<{ tempPassword: string }> {
  return await http<{ ok: true; tempPassword: string }>('POST', `/api/users/${id}/reset-password`);
}

// ------- Bookings (admin) -------
export async function fetchAllBookings(): Promise<Booking[]> {
  const out = await http<{ bookings: Booking[] }>('GET', '/api/bookings');
  return out.bookings;
}

export async function decideBooking(id: string, decision: 'approved' | 'declined', note = ''): Promise<void> {
  await http<void>('POST', `/api/bookings/${id}/decision`, { decision, note });
}

export async function decideOccurrence(occId: string, decision: 'approved' | 'declined', note = ''): Promise<void> {
  await http<void>('POST', `/api/bookings/occurrences/${occId}`, { decision, note });
}

// ------- Session types -------
// Admin-managed priceable SKUs. GET is public (trainee booking modal
// fetches the same endpoint and filters client-side). Writes require
// the admin bearer token.
export async function fetchSessionTypes(): Promise<SessionType[]> {
  try {
    const out = await http<{ sessionTypes: SessionType[] }>('GET', '/api/session-types');
    return out.sessionTypes ?? [];
  } catch {
    const tree = readLocal();
    return tree.sessionTypes ?? structuredClone(DEFAULT_SESSION_TYPES);
  }
}

export async function createSessionType(input: Omit<SessionType, 'createdAt' | 'updatedAt'> & { id?: string }): Promise<SessionType> {
  try {
    const out = await http<{ sessionType: SessionType }>('POST', '/api/session-types', input);
    return out.sessionType;
  } catch (err) {
    // Only swallow network/offline errors. Server validation (400),
    // auth (401), slug conflicts (409), etc. must bubble up so the
    // admin UI can show them.
    if ((err as { offline?: boolean }).offline) {
      const tree = readLocal();
      if (!Array.isArray(tree.sessionTypes)) tree.sessionTypes = [];
      const st: SessionType = {
        id: input.id && input.id.length > 0 ? input.id : crypto.randomUUID(),
        slug: input.slug,
        name: input.name,
        description: input.description,
        priceHuf: input.priceHuf,
        discipline: input.discipline,
        isActive: input.isActive,
        sortOrder: input.sortOrder,
      };
      tree.sessionTypes.push(st);
      writeLocal(tree);
      return st;
    }
    throw err;
  }
}

export async function updateSessionType(st: SessionType): Promise<SessionType> {
  try {
    const out = await http<{ sessionType: SessionType }>('PUT', `/api/session-types/${st.id}`, st);
    return out.sessionType;
  } catch (err) {
    if ((err as { offline?: boolean }).offline) {
      const tree = readLocal();
      if (!Array.isArray(tree.sessionTypes)) tree.sessionTypes = [];
      const i = tree.sessionTypes.findIndex(x => x.id === st.id);
      if (i >= 0) tree.sessionTypes[i] = st; else tree.sessionTypes.push(st);
      writeLocal(tree);
      return st;
    }
    throw err;
  }
}

export async function deleteSessionType(id: string): Promise<void> {
  try {
    await http<void>('DELETE', `/api/session-types/${id}`);
  } catch (err) {
    // Only swallow network errors. 409 in_use MUST bubble up so the
    // admin UI can offer soft-disable instead of silently dropping
    // the FK link in the DB.
    if ((err as { offline?: boolean }).offline) {
      const tree = readLocal();
      if (Array.isArray(tree.sessionTypes)) {
        tree.sessionTypes = tree.sessionTypes.filter(x => x.id !== id);
        writeLocal(tree);
      }
      return;
    }
    throw err;
  }
}
// =============================================================
// Tenant admin endpoints — fetch / patch tenant, business, splash.
// All PATCHes require the admin bearer token (already in headers
// via authHeaders()).
// =============================================================

export interface TenantThemeInfo {
  id: 'sloth-forest' | 'studio-333' | 'midnight-blue' | 'sunset-coral' | 'lavender-mist' | 'mint-breeze' | 'charcoal-slate' | 'cream-pop';
  label: string;
  swatches: string[];
}

export const TENANT_THEMES: TenantThemeInfo[] = [
  { id: 'sloth-forest',   label: 'Sloth Forest',     swatches: ['#FDF6E3', '#F6C775', '#2D4231', '#191913'] },
  { id: 'studio-333',     label: 'Studio 333',        swatches: ['#FFFFFF', '#FFD601', '#0A0A0A', '#E6E6E6'] },
  { id: 'midnight-blue',  label: 'Midnight Blue',     swatches: ['#0F172A', '#38BDF8', '#E2E8F0', '#334155'] },
  { id: 'sunset-coral',   label: 'Sunset Coral',      swatches: ['#FFE4E1', '#FF6B6B', '#2D2D2D', '#FFCFC8'] },
  { id: 'lavender-mist',  label: 'Lavender Mist',     swatches: ['#F3E8FF', '#A78BFA', '#1E1B4B', '#D8C4F0'] },
  { id: 'mint-breeze',    label: 'Mint Breeze',       swatches: ['#ECFDF5', '#10B981', '#064E3B', '#A7E8C9'] },
  { id: 'charcoal-slate', label: 'Charcoal Slate',    swatches: ['#1F2937', '#F59E0B', '#F9FAFB', '#4B5563'] },
  { id: 'cream-pop',      label: 'Cream Pop',         swatches: ['#FFF7ED', '#F97316', '#7C2D12', '#FED7AA'] },
];

export interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  status: string;
  mode: string;
  theme: string;
  defaultLocale: string;
  /** Tenant-level site settings (favicon, theme color, OG image). */
  faviconUrl?: string | null;
  themeColor?: string | null;
  ogImageUrl?: string | null;
  siteName?: string | null;
}
export interface TenantBusinessInfo {
  vertical: string;
  businessName: string;
  tagline: string;
  description: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  website: string;
  hours: Record<string, string>;
  social: Record<string, string>;
  logoUrl: string;
  currency: string;
  timezone: string;
  locale: string;
}
export interface TenantSplashInfo {
  enabled: boolean;
  preset: 'studio-picker' | 'promotion' | 'info';
  custom: any;
}

export interface TenantFullInfo {
  tenant: TenantInfo;
  business: TenantBusinessInfo | null;
  splash: TenantSplashInfo | null;
}

export async function fetchTenant(): Promise<TenantFullInfo> {
  const r = await fetch('/api/tenant');
  if (!r.ok) throw new Error('fetchTenant failed');
  return r.json();
}

export async function updateTenant(patch: Partial<TenantInfo>): Promise<void> {
  const r = await fetch('/api/tenant', {
    method: 'PATCH',
    headers: { ...authHeaders(), 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error('updateTenant failed: ' + (await r.text()));
}

export async function fetchTenantBusiness(): Promise<TenantBusinessInfo | null> {
  const r = await fetch('/api/tenant-business');
  if (!r.ok) throw new Error('fetchTenantBusiness failed');
  const j = await r.json() as { business: TenantBusinessInfo | null };
  return j.business;
}

export async function updateTenantBusiness(patch: Partial<TenantBusinessInfo>): Promise<void> {
  const r = await fetch('/api/tenant-business', {
    method: 'PATCH',
    headers: { ...authHeaders(), 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error('updateTenantBusiness failed: ' + (await r.text()));
}

export async function fetchTenantSplash(): Promise<TenantSplashInfo> {
  const r = await fetch('/api/tenant-splash');
  if (!r.ok) throw new Error('fetchTenantSplash failed');
  const j = await r.json() as { splash: TenantSplashInfo };
  return j.splash || { enabled: false, preset: 'studio-picker', custom: {} };
}

export async function updateTenantSplash(patch: Partial<TenantSplashInfo>): Promise<void> {
  const r = await fetch('/api/tenant-splash', {
    method: 'PATCH',
    headers: { ...authHeaders(), 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!r.ok) throw new Error('updateTenantSplash failed: ' + (await r.text()));
}

export async function registerUser(input: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}): Promise<{ ok: true; user: any }> {
  const r = await fetch('/api/auth/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({ error: 'unknown' }));
    throw new Error(e.error || 'register failed');
  }
  return r.json();
}
